import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import {
  TabsContainer,
  TabsHeader,
  TabContent,
  RightSide,
} from "./UnifiedProfileTabs.styled";
import Tabs2 from "../../../../../elements/v2/Tabs2/Tabs2";
import TimelineBox from "../Assistance/TimelineBox";

import { useStore } from "../../../../../hooks";
import UCPChatbox from "../UCPChatbox/UCPChatbox";
import HistoryTab from "../Assistance/HistoryTab";
import { FormControlLabel, Switch } from "@mui/material";
import SearchComponent from "../../../../../elements/v2/SearchComponent";
import { TimelineSearch } from "./components";
import useSearchNavigation from "../hooks/useSearchNavigation";

const tabsEnum = {
  CHAT: 0,
  HISTORY: 1,
  TIMELINE: 2,
};

const tabs = [
  { label: "Chat", disabled: false },
  { label: "History", disabled: false },
  { label: "Timeline", disabled: false },
];

const UnifiedProfileTabs = observer(({ showBorder, width, onSwitchChange }) => {
  const { authStore, assistanceUCPStore, historyAgentStore, alertStore } = useStore();
  const { selectedAccount } = authStore;
  const [activeTab, setActiveTab] = useState(tabsEnum.CHAT);
  const [availableConversations, setAvailableConversations] = useState([]);
  const contactId = alertStore.segmentId;
  
  useEffect(() => {
    if (assistanceUCPStore.selectedTab === 'Chat' && activeTab !== tabsEnum.CHAT) {
      setActiveTab(tabsEnum.CHAT);
    } else if (assistanceUCPStore.selectedTab === 'History' && activeTab !== tabsEnum.HISTORY) {
      setActiveTab(tabsEnum.HISTORY);
    } else if (assistanceUCPStore.selectedTab === 'Timeline' && activeTab !== tabsEnum.TIMELINE) {
      setActiveTab(tabsEnum.TIMELINE);
    }
  }, [assistanceUCPStore.selectedTab, activeTab]);

  const handleTabChange = (tabLabel) => {
    setActiveTab(tabLabel);

    if (tabLabel === tabsEnum.CHAT) {
      assistanceUCPStore.setSelectedTab('Chat');
    } else if (tabLabel === tabsEnum.HISTORY) {
      assistanceUCPStore.setSelectedTab('History');
    } else if (tabLabel === tabsEnum.TIMELINE) {
      assistanceUCPStore.setSelectedTab('Timeline');
    }
  };

  useEffect(() => {
    if (activeTab !== tabsEnum.HISTORY && assistanceUCPStore.searchTerm) {
      assistanceUCPStore.resetSearch();
    }
  }, [activeTab, assistanceUCPStore]);


  useEffect(() => {
    if (historyAgentStore.conversations && historyAgentStore.conversations.length > 0) {
      const filteredByContact = historyAgentStore.conversations.filter((item) => {
        return item.metadata && item.metadata.ref_id === contactId;
      });

      setAvailableConversations(filteredByContact);

      assistanceUCPStore.setFilteredConversations(filteredByContact);
    }
  }, [historyAgentStore.conversations, contactId, assistanceUCPStore]);


  /**
   * Monitoriza cambios en el conversationID para mantener las conversaciones actualizadas.
   * Verifica si la conversación existe en las filtradas, y si no, la busca en la lista completa.
   * Si pertenece al contacto actual, la añade al principio de la lista filtrada.
   */
  useEffect(() => {
    const conversationId = assistanceUCPStore.conversationID;
    if (!conversationId) return;

    if (!historyAgentStore.conversations || historyAgentStore.conversations.length === 0) return;
    if (!assistanceUCPStore.filteredConversations) return;

    const timeoutId = setTimeout(() => {
      const exists = assistanceUCPStore.filteredConversations.some(conv => conv.id === conversationId);
      if (exists) return;

      const newConversation = historyAgentStore.conversations.find(conv => conv.id === conversationId);
      if (!newConversation) return;

      if (newConversation.metadata && newConversation.metadata.ref_id === contactId) {
        const updatedConversations = [newConversation, ...assistanceUCPStore.filteredConversations];
        assistanceUCPStore.setFilteredConversations(updatedConversations);
        setAvailableConversations(updatedConversations);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistanceUCPStore.conversationID]);

  const searchConfig = {
    searchTerm: assistanceUCPStore.searchTerm,
    highlightedResult: assistanceUCPStore.highlightedResult,
    setSearchTerm: (term) => assistanceUCPStore.setSearchTerm(term),
    setHighlightedResult: (index) => assistanceUCPStore.setHighlightedResult(index),
    setFilteredConversations: (conversations) => assistanceUCPStore.setFilteredConversations(conversations)
  };

  const {
    currentResultIndex,
    handleSearch,
    handleClearSearch,
    navigateNext,
    navigatePrevious,
  } = useSearchNavigation(
      availableConversations,
      searchConfig
  );
  

  const actualFilteredCount = assistanceUCPStore.filteredCount;
  const actualTotalCount = assistanceUCPStore.totalCount;

  return (
      <TabsContainer>
        <TabsHeader showBorder={showBorder} width={width}>
          <Tabs2
              tabs={tabs}
              defaultTab={activeTab || tabsEnum.CHAT}
              onToggleTab={handleTabChange}
          />
          <RightSide>
            {activeTab === tabsEnum.CHAT && onSwitchChange && (
                <FormControlLabel
                    control={<Switch />}
                    label="Beta"
                    onChange={onSwitchChange}
                />
            )}
            {activeTab === tabsEnum.HISTORY && (
                <SearchComponent
                    searchTerm={assistanceUCPStore.searchTerm}
                    onSearchChange={handleSearch}
                    onClear={handleClearSearch}
                    onNavigateNext={navigateNext}
                    onNavigatePrevious={navigatePrevious}
                    filteredCount={actualFilteredCount}
                    totalCount={actualTotalCount}
                    currentResultIndex={currentResultIndex - 1}
                    debounceDelay={0}
                />
            )}
            {activeTab === tabsEnum.TIMELINE && <TimelineSearch />}
          </RightSide>
        </TabsHeader>

        <TabContent>
          <Tabs2.Outlet show={activeTab === tabsEnum.CHAT}>
            <UCPChatbox selectedAccount={selectedAccount} />
          </Tabs2.Outlet>

          <Tabs2.Outlet show={activeTab === tabsEnum.HISTORY}>
            <HistoryTab
                selectedAgent={selectedAccount}
                onTabChange={handleTabChange}
            />
          </Tabs2.Outlet>

          <Tabs2.Outlet show={activeTab === tabsEnum.TIMELINE}>
            <>
              <TimelineBox matches={assistanceUCPStore.timelineMatches} />
            </>
          </Tabs2.Outlet>
        </TabContent>
      </TabsContainer>
  );
});

export default UnifiedProfileTabs;
